import { useRef, useState } from 'react';
import { Avatar, Box, CircularProgress, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useUser } from '@clerk/clerk-react';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';

export type ClerkProfilePhotoEditorProps = {
  /** Avatar width and height in pixels */
  avatarSize?: number;
};

/**
 * Clerk-backed profile photo with file picker and upload progress.
 * Edit button uses stopPropagation so it can sit inside clickable parents (e.g. settings sidebar header).
 */
export function ClerkProfilePhotoEditor({ avatarSize = 64 }: ClerkProfilePhotoEditorProps) {
  const t = useTranslations();
  const notifications = useNotifications();
  const { user: clerkUser } = useUser();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !clerkUser) return;
    setIsUploading(true);
    try {
      await clerkUser.setProfileImage({ file });
      notifications.notifySuccess(t('user.notification.profilePhotoUploadSuccess'));
    } catch (error) {
      console.error('Failed to update profile image:', error);
      notifications.notifyError(t('user.notification.profilePhotoUploadError'));
    } finally {
      setIsUploading(false);
    }
    event.target.value = '';
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const imageUrl = clerkUser?.imageUrl;
  const progressSize = Math.max(20, Math.round(avatarSize * 0.45));
  const editBtnSize = Math.max(24, Math.round(avatarSize * 0.55));

  return (
    <>
      <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" style={{ display: 'none' }} />
      <Box sx={{ position: 'relative', width: avatarSize, height: avatarSize, flexShrink: 0 }}>
        <Avatar
          src={imageUrl}
          alt={t('user.profile.userInfo.profilePhoto')}
          sx={{ width: avatarSize, height: avatarSize }}
        />
        {isUploading ? (
          <CircularProgress
            size={progressSize}
            sx={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              bgcolor: 'background.default',
              borderRadius: '50%',
              padding: '2px',
              color: 'primary.main',
            }}
          />
        ) : (
          <IconButton
            onClick={handleEditClick}
            size="small"
            aria-label={t('user.profile.userInfo.changeProfilePhoto')}
            sx={{
              'position': 'absolute',
              'bottom': -4,
              'right': -4,
              'bgcolor': 'background.paper',
              'border': '1px solid',
              'borderColor': 'divider',
              '&:hover': { bgcolor: 'action.hover' },
              'width': editBtnSize,
              'height': editBtnSize,
            }}
          >
            <EditIcon sx={{ fontSize: Math.max(14, avatarSize * 0.35) }} />
          </IconButton>
        )}
      </Box>
    </>
  );
}
